import React, { FC, useEffect, useState } from 'react';

import { CloudProviders } from '@ariksa/cloud-account';
import { Badge, Box, HStack, Link, Stack } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { BASE_URL } from 'api/service_urls';
import { Card, PageHeaderWithIcon } from 'components/DataDisplay';
import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import { FilterLabelWrapper } from 'components/DataDisplay/Box/BorderedBox/FilterLabelWrapper';
import { AddButton, ButtonGroup, ButtonGroupItem } from 'components/DataEntry';
import { ClockIcon } from 'components/Icons';
import { infoToast, successToast } from 'components/Toast/toasterFunctions';
import { useSearchParamFunctions } from 'containers/App/hooks/useSearchParamFunctions';
import { selectApp } from 'containers/App/selectors';
import { JitAccounts } from 'containers/Jit/Setup/Components/JitAccounts';
import { ConnectSlackButton } from 'containers/Jit/Setup/Components/Utils/ConnectSlackButton';
import { useSearchParams } from 'hooks/useSearchParams';
import request from 'utils/request';

import { jitSetupViewByOptions } from './utils';

interface Props {
  basePath?: string;
}

export const JITConfiguration: FC<Props> = ({ basePath = '/jit/setup' }) => {
  const { cloudAccounts } = useSelector(selectApp);
  const dataAccounts = cloudAccounts.data.filter(
    account =>
      account.cloud_type === CloudProviders.Snowflake &&
      account.data_access_mgmt_status !== 'SUCCESS',
  );

  const [viewBy, setViewBy] = React.useState<ButtonGroupItem>(
    jitSetupViewByOptions[0],
  );
  const { updateValueInURL } = useSearchParamFunctions();
  const params = useSearchParams<{ tab?: string }>();
  const navigate = useNavigate();

  const [slackConnected, setSlackConnected] = useState<boolean | undefined>(
    undefined,
  );

  useEffect(() => {
    const selectedViewBy =
      params?.tab === jitSetupViewByOptions[1]?.value
        ? jitSetupViewByOptions[1]
        : jitSetupViewByOptions[0];

    setViewBy(selectedViewBy);
  }, [params?.tab]);

  useEffect(() => {
    const fetchSlackStatus = async () => {
      try {
        const res = await request(`${BASE_URL}/v1/jit/slack/workspace`);
        setSlackConnected((res?.number_of_workspaces ?? 0) > 0);
      } catch {
        setSlackConnected(undefined);
      }
    };

    fetchSlackStatus();
  }, []);

  // Handle the return from the Slack install (Model B redirects here).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const slack = params.get('slack');
    if (!slack) {
      return;
    }
    if (slack === 'connected') {
      const team = params.get('team');
      const teamId = params.get('team_id');
      const appId = params.get('app');
      successToast({
        title: team ? `Slack connected to ${team}` : 'Slack connected',
        description:
          teamId && appId ? (
            <Link
              href={`slack://open?team=${teamId}&id=${appId}`}
              color="primary"
            >
              Open in Slack
            </Link>
          ) : undefined,
        duration: 8000,
      });
    } else if (slack === 'error') {
      infoToast({
        title: 'Could not connect Slack',
        description: params.get('reason') || 'Please try again.',
      });
    }
    // Drop only the Slack callback params so the active tenant and account
    // context survives the redirect.
    ['slack', 'team', 'team_id', 'app', 'reason'].forEach(param =>
      params.delete(param),
    );
    navigate(
      {
        pathname: '/jit/setup',
        search: params.toString(),
      },
      { replace: true },
    );
  }, [navigate]);

  return (
    <Card>
      <Stack w="full" spacing={5}>
        <HStack justify="space-between">
          <Box>
            <PageHeaderWithIcon
              label="Just In Time Configuration"
              icon={<ClockIcon />}
            />
          </Box>
          <HStack>
            {slackConnected && (
              <Badge colorScheme="green" px={2} py={1} borderRadius="md">
                Slack connected
              </Badge>
            )}
            <ConnectSlackButton
              label={slackConnected ? 'Reconnect Slack' : 'Connect Slack'}
            />

            <AddButton
              label="Enable JIT"
              isDisabled={viewBy.value === 'data' && !dataAccounts.length}
              onClick={() => {
                if (viewBy.value === 'data' && dataAccounts.length) {
                  navigate(
                    `${basePath}/enable-data-access/${CloudProviders.Snowflake}/${dataAccounts[0].uuid}`,
                  );
                  return;
                }
                navigate(`${basePath}/add`);
              }}
            />
          </HStack>
        </HStack>
        <BorderedBox>
          <HStack justify="space-between">
            <HStack>
              <FilterLabelWrapper>VIEW BY</FilterLabelWrapper>
              <ButtonGroup
                items={jitSetupViewByOptions}
                value={viewBy}
                onClick={item => updateValueInURL('tab', item.value)}
              />
            </HStack>
            {/*<PrimaryIconButton
              aria-label="approve=policy"
              icon={<ThumbsUpIcon />}
              onClick={() => navigate('/jit/setup/approval-policy')}
              showGrayIcon
            />*/}
          </HStack>
        </BorderedBox>
        <Box flex={1}>
          <JitAccounts viewBy={viewBy.value} />
        </Box>
      </Stack>
    </Card>
  );
};
